import {
  RelatedAINote,
  RelatedAnalytics,
  RelatedCompany,
  RelatedDeal,
  RelatedDocument,
  RelatedFolder,
  RelatedFund,
  RelatedItem,
  RelatedMeeting,
  RelatedMember,
  RelatedNote,
  RelatedOpportunity,
  RelatedPerson,
  RelatedThemes,
  RelatedThread,
  RelatedWebpage,
} from "@app-types/related-items.type";

export function matchRelatedItem<T = RelatedItem, R = any>(
  item: T,
  {
    onDeal,
    onDocument,
    onMeeting,
    onWebpage,
    onNote,
    onFolder,
    onCompanies,
    onAINote,
    onPeople,
    onThemes,
    onThread,
    onAnalytics,
  }: {
    onDeal?: (item: RelatedDeal) => R;
    onDocument?: (item: RelatedDocument) => R;
    onMeeting?: (item: RelatedMeeting) => R;
    onWebpage?: (item: RelatedWebpage) => R;
    onAINote?: (item: RelatedAINote) => R;
    onNote?: (item: RelatedNote) => R;
    onFolder?: (item: RelatedFolder) => R;
    onCompanies?: (item: RelatedCompany) => R;
    onPeople?: (item: RelatedPerson) => R;
    onThemes?: (item: RelatedThemes) => R;
    onThread?: (item: RelatedThread) => R;
    onAnalytics?: (item: RelatedAnalytics) => R;
  }
): R | null {
  if (isDeal(item)) {
    return onDeal?.(item) ?? null;
  } else if (isDocument(item)) {
    return onDocument?.(item) ?? null;
  } else if (isMeeting(item)) {
    return onMeeting?.(item) ?? null;
  } else if (isWebpage(item)) {
    return onWebpage?.(item) ?? null;
  } else if (isAINote(item)) {
    return onAINote?.(item) ?? null;
  } else if (isNote(item)) {
    return onNote?.(item) ?? null;
  } else if (isFolder(item)) {
    return onFolder?.(item) ?? null;
  } else if (isThemes(item)) {
    return onThemes?.(item) ?? null;
  } else if (isThread(item)) {
    return onThread?.(item) ?? null;
  } else if (isCompany(item)) {
    return onCompanies?.(item) ?? null;
  } else if (isPerson(item)) {
    return onPeople?.(item) ?? null;
  } else if (isAnalytics(item)) {
    return onAnalytics?.(item) ?? null;
  }
  return null;
}

export const isAnalytics = (related_item: any): related_item is RelatedAnalytics =>
  !!related_item && related_item?.source_type === "analytics";

export const isMeeting = (related_item: any): related_item is RelatedMeeting =>
  !!related_item && "start_time" in related_item && !!related_item.start_time;

export const isDeal = (related_item: any): related_item is RelatedDeal =>
  !!related_item &&
  related_item?.object_type !== "fundraising.opportunity" &&
  (("stage" in related_item && !!related_item.stage) || related_item?.object_type === "deals.deal");

export const isDocument = (related_item: any): related_item is RelatedDocument =>
  !!related_item &&
  (related_item.object_type === "documents.documentfile" ||
    related_item?.source_type === "document" ||
    (!!related_item.file_name && typeof related_item.file_name === "string") ||
    (!!related_item.file && typeof related_item.file === "string"));

export const isMember = (related_item: any): related_item is RelatedMember =>
  !!related_item && related_item.object_type === "accounts.user";

export const isFolder = (related_item: any): related_item is RelatedFolder =>
  !!related_item && "name" in related_item && related_item?.object_type === "documents.folder";

export const isWebpage = (related_item: any): related_item is RelatedWebpage =>
  !!related_item && "favicon" in related_item;

export const isNote = (related_item: any): related_item is RelatedNote =>
  !!related_item && "access" in related_item;

export const isCompany = (related_item: any): related_item is RelatedCompany =>
  (!!related_item && "website" in related_item) ||
  related_item?.object_type === "companies.company";

export const isAINote = (related_item: any): related_item is RelatedAINote =>
  Boolean(related_item?.object_type === "notes.ainote" || related_item?.object_type === "ainote");

export const isPerson = (related_item: any): related_item is RelatedPerson => {
  return (
    !!related_item &&
    (Boolean(related_item?.object_type === "people.person") ||
      related_item?.source_type === "person" ||
      "email" in related_item ||
      !!related_item.email)
  );
};

export const isThemes = (related_item: any): related_item is RelatedThemes => {
  return (
    !!related_item &&
    (("level" in related_item && !!related_item.level) ||
      related_item?.object_type === "sourcing.theme" ||
      related_item?.content_type === "theme" ||
      (related_item?.object && "level" in related_item.object && !!related_item.object.level))
  );
};

export const isThread = (related_item: any): related_item is RelatedThread => {
  return (
    Boolean(related_item?.object_type === "emails.thread") || related_item?.source_type === "thread"
  );
};

export const isFund = (related_item: any): related_item is RelatedFund =>
  !!related_item && related_item?.object_type === "fundraising.fund";

export const isOpportunity = (related_item: any): related_item is RelatedOpportunity =>
  !!related_item && related_item?.object_type === "fundraising.opportunity";

export function getSearchGroups(items: RelatedItem[]) {
  return items
    .reduce(
      (acc, item) => {
        matchRelatedItem(item, {
          onDeal() {
            acc[0][1].push(item);
          },
          onDocument() {
            acc[1][1].push(item);
          },
          onMeeting() {
            acc[2][1].push(item);
          },
          onPeople() {
            acc[3][1].push(item);
          },
          onCompanies() {
            acc[4][1].push(item);
          },
        });

        return acc;
      },
      [
        ["Deals", [] as RelatedItem[]],
        ["Documents", [] as RelatedItem[]],
        ["Meetings", [] as RelatedItem[]],
        ["People", [] as RelatedItem[]],
        ["Companies", [] as RelatedItem[]],
      ] as const
    )
    .filter(([, items]) => items.length > 0);
}

export function getSearchOrderedItems(groups: ReturnType<typeof getSearchGroups>) {
  return groups.flatMap(([, items]) => items);
}
